import { FormEvent, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  api,
  ApiError,
  type CreateDisplayResponse,
  type DigitStyle,
  type DisplayChannel,
  type DisplaySummary,
  type GameSummary,
  type LayoutTemplate,
  type ScoreboardConfig,
  type ThemeKey,
  type Venue,
} from '../api';
import { AppShell } from '../components/AppShell';

const THEME_OPTIONS: { value: ThemeKey; label: string; swatch: string }[] = [
  { value: 'classic', label: 'Classic Red', swatch: '#8b1a1a' },
  { value: 'maroon', label: 'Maroon', swatch: '#4a1028' },
  { value: 'navy', label: 'Navy', swatch: '#1a2744' },
  { value: 'forest', label: 'Forest', swatch: '#1a3a2a' },
  { value: 'royal', label: 'Royal', swatch: '#1a3a8a' },
  { value: 'arena-led', label: 'Arena LED', swatch: '#0a0c10' },
  { value: 'premium-glass', label: 'Premium Glass', swatch: '#0a0f1c' },
  { value: 'angular', label: 'Angular', swatch: '#0c1018' },
  { value: 'daylight', label: 'Daylight (light)', swatch: '#eef2f8' },
  { value: 'electric', label: 'Electric', swatch: '#0c163a' },
  { value: 'mono', label: 'Mono (light)', swatch: '#fafbfc' },
  { value: 'retro-led', label: 'Retro LED', swatch: '#000000' },
  { value: 'broadcast', label: 'Broadcast', swatch: '#0c1220' },
  { value: 'stadium-gold', label: 'Stadium Gold', swatch: '#06140e' },
  { value: 'carbon', label: 'Carbon', swatch: '#15171c' },
  { value: 'sunset', label: 'Sunset (light)', swatch: '#ff7e5f' },
  { value: 'frost', label: 'Frost (light)', swatch: '#dbeafe' },
  { value: 'volt', label: 'Volt', swatch: '#0b0b0d' },
];

const TEMPLATE_OPTIONS: { value: string; label: string }[] = [
  { value: 'classic-gym-board', label: 'Classic Gym Board' },
  { value: 'simple-score-clock', label: 'Simple — Score + Clock' },
  { value: 'video-player-spotlight', label: 'Video — Player Spotlight' },
  { value: 'jumbotron', label: 'Jumbotron — Full Video' },
  { value: 'arena-led-full', label: 'Arena LED — Full Board' },
  { value: 'arena-led-jumbo', label: 'Arena LED — Jumbotron' },
  { value: 'arena-led-stats', label: 'Arena LED — Stats Focus' },
  { value: 'arena-led-compact', label: 'Arena LED — Compact' },
  { value: 'premium-glass-full', label: 'Premium Glass — Full Board' },
  { value: 'premium-glass-jumbo', label: 'Premium Glass — Jumbotron' },
  { value: 'premium-glass-stats', label: 'Premium Glass — Stats Focus' },
  { value: 'premium-glass-compact', label: 'Premium Glass — Compact' },
  { value: 'angular-full', label: 'Angular — Full Board' },
  { value: 'angular-jumbo', label: 'Angular — Jumbotron' },
  { value: 'angular-stats', label: 'Angular — Stats Focus' },
  { value: 'angular-compact', label: 'Angular — Compact' },
];

const CHANNEL_OPTIONS: { value: DisplayChannel; label: string }[] = [
  { value: 'SCOREBOARD', label: 'Scoreboard' },
  { value: 'AUX_CLOCK', label: 'Auxiliary clock' },
  { value: 'STANDINGS', label: 'Standings & schedule' },
  { value: 'STREAM_OVERLAY', label: 'Stream overlay' },
  { value: 'MENU', label: 'Menu / signage' },
  { value: 'AD_REEL', label: 'Ad reel' },
  { value: 'SIGNAGE', label: 'Signage' },
  { value: 'STATS_BOARD', label: 'Stats board' },
];

export function VenueDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [venue, setVenue] = useState<Venue | null>(null);
  const [displays, setDisplays] = useState<DisplaySummary[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [justCreated, setJustCreated] = useState<CreateDisplayResponse | null>(
    null,
  );

  async function load() {
    if (!id) return;
    try {
      setErr(null);
      const [v, ds] = await Promise.all([
        api.getVenue(id),
        api.listVenueDisplays(id),
      ]);
      setVenue(v);
      setDisplays(ds);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'failed_to_load');
    }
  }

  useEffect(() => {
    load();
  }, [id]);

  if (err) {
    return (
      <AppShell>
        <div className="card text-red-300">{err}</div>
      </AppShell>
    );
  }
  if (!venue || !displays) {
    return (
      <AppShell>
        <div className="text-muted">Loading…</div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="space-y-6">
        <div>
          <Link to="/venues" className="text-sm text-muted hover:text-text">
            ← Venues
          </Link>
        </div>

        <div>
          <h1 className="text-3xl font-bold tracking-tight">{venue.name}</h1>
          <p className="mt-1 text-muted">
            {venue.address ?? 'No address set'} ·{' '}
            <span className="font-mono">{venue.timezone}</span>
            {venue.capacity != null && <> · cap. {venue.capacity.toLocaleString()}</>}
          </p>
        </div>

        {justCreated && (
          <PairingCodeCard
            data={justCreated}
            onDismiss={async () => {
              setJustCreated(null);
              await load();
            }}
          />
        )}

        <div className="flex items-end justify-between">
          <div>
            <h2 className="text-xl font-semibold">Displays</h2>
            <p className="mt-1 text-sm text-muted">
              Screens registered to this venue. Pair any screen by opening
              <span className="font-mono"> display.scorelync.com </span>
              and entering the 6-character code.
            </p>
          </div>
          {!showForm && !justCreated && (
            <button
              className="btn btn-primary"
              onClick={() => setShowForm(true)}
            >
              + Add display
            </button>
          )}
        </div>

        {showForm && (
          <AddDisplayForm
            venueId={venue.id}
            onDone={async (created) => {
              setShowForm(false);
              setJustCreated(created);
              await load();
            }}
            onCancel={() => setShowForm(false)}
          />
        )}

        {displays.length === 0 && !showForm && !justCreated ? (
          <div className="card text-center">
            <div className="mx-auto mb-3 text-4xl">📺</div>
            <h3 className="text-lg font-semibold">No displays yet</h3>
            <p className="mt-1 text-sm text-muted">
              Add a display, get a 6-character code, enter it on any screen at
              <span className="font-mono"> display.scorelync.com</span>.
            </p>
            <button
              className="btn btn-primary mt-4"
              onClick={() => setShowForm(true)}
            >
              + Add your first display
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {displays.map((d) => (
              <DisplayCard key={d.id} display={d} onChanged={load} />
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
}

function statusColor(status: DisplaySummary['status']): string {
  switch (status) {
    case 'ONLINE':
      return 'bg-emerald-500';
    case 'OFFLINE':
      return 'bg-red-500';
    case 'PAIRING':
      return 'bg-amber-400';
  }
}

function DisplayCard({
  display,
  onChanged,
}: {
  display: DisplaySummary;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [code, setCode] = useState<{ code: string; expiresAt: string } | null>(
    null,
  );
  const [customizing, setCustomizing] = useState(false);
  const [games, setGames] = useState<GameSummary[] | null>(null);

  useEffect(() => {
    if (display.channelType !== 'SCOREBOARD') return;
    api.listGames().then(setGames).catch(() => setGames([]));
  }, [display.channelType]);

  const assignedGameId = display.displayConfig?.assignedGameId ?? '';
  const assignedGame = games?.find((g) => g.id === assignedGameId);

  async function setAssignedGame(gameId: string) {
    if (gameId === (display.displayConfig?.assignedGameId ?? '')) return;
    setBusy(true);
    try {
      const next: ScoreboardConfig = {
        ...(display.displayConfig ?? {}),
        ...(gameId ? { assignedGameId: gameId } : { assignedGameId: undefined }),
      };
      await api.updateDisplay(display.id, { displayConfig: next });
      onChanged();
    } finally {
      setBusy(false);
    }
  }

  async function regenerate() {
    if (display.status === 'ONLINE' || display.status === 'OFFLINE') {
      if (
        !confirm(
          `Re-pair "${display.name}"? The currently paired screen will be kicked off and need to enter a new code.`,
        )
      )
        return;
    }
    setBusy(true);
    try {
      const r = await api.regeneratePairingCode(display.id);
      setCode({ code: r.pairingCode, expiresAt: r.pairingExpiresAt });
    } finally {
      setBusy(false);
      onChanged();
    }
  }

  async function setChannel(channel: DisplayChannel) {
    if (channel === display.channelType) return;
    setBusy(true);
    try {
      await api.updateDisplay(display.id, { channelType: channel });
      onChanged();
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (
      !confirm(
        `Remove "${display.name}"? The display will lose its pairing and need a new code.`,
      )
    )
      return;
    setBusy(true);
    try {
      await api.deleteDisplay(display.id);
      onChanged();
    } finally {
      setBusy(false);
    }
  }

  const last = display.lastHeartbeat
    ? new Date(display.lastHeartbeat).toLocaleString()
    : '—';

  return (
    <div className="card">
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <span
            className={`inline-block h-2 w-2 shrink-0 rounded-full ${statusColor(display.status)}`}
          />
          <h3 className="truncate text-lg font-semibold">{display.name}</h3>
        </div>
        <button
          onClick={remove}
          disabled={busy}
          className="shrink-0 text-xs text-muted transition hover:text-red-400"
        >
          Delete
        </button>
      </div>
      <div className="mt-4">
        <label className="label">Channel</label>
        <select
          className="input"
          value={display.channelType}
          disabled={busy}
          onChange={(e) => setChannel(e.target.value as DisplayChannel)}
        >
          {CHANNEL_OPTIONS.map((c) => (
            <option key={c.value} value={c.value}>
              {c.label}
            </option>
          ))}
        </select>
        <p className="mt-1 text-xs text-muted">
          The display switches within ~5 seconds (heartbeat interval).
        </p>
      </div>

      {display.channelType === 'SCOREBOARD' && (
        <div className="mt-4">
          <label className="label">Linked game</label>
          <select
            className="input"
            value={assignedGameId}
            disabled={busy || !games}
            onChange={(e) => setAssignedGame(e.target.value)}
          >
            <option value="">— Not linked (sample data) —</option>
            {(games ?? []).map((g) => (
              <option key={g.id} value={g.id}>
                {g.status === 'LIVE' ? '● LIVE  ' : ''}
                {g.homeTeam.name} vs {g.awayTeam.name}
                {g.scheduledAt
                  ? ` · ${new Date(g.scheduledAt).toLocaleString()}`
                  : ''}
              </option>
            ))}
          </select>
          {assignedGame ? (
            <p className="mt-1 text-xs text-muted">
              Displaying live state of{' '}
              <Link
                to={`/games/${assignedGame.id}`}
                className="text-brand-light hover:text-brand"
              >
                {assignedGame.homeTeam.name} vs {assignedGame.awayTeam.name}
              </Link>
              .
            </p>
          ) : (
            <p className="mt-1 text-xs text-amber-300/80">
              No game linked — display will show sample data. Pick a game
              above to drive live scoring.
            </p>
          )}
        </div>
      )}
      <dl className="mt-4 space-y-2 text-sm">
        <Row label="Status">{display.status.toLowerCase()}</Row>
        <Row label="Last heartbeat">{last}</Row>
        {display.resolutionW && display.resolutionH && (
          <Row label="Resolution">
            <span className="font-mono">
              {display.resolutionW}×{display.resolutionH}
            </span>
          </Row>
        )}
      </dl>
      {code ? (
        <div className="mt-4 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-center">
          <div className="text-xs uppercase tracking-wider text-amber-300">
            New pairing code
          </div>
          <div className="mt-1 font-mono text-2xl font-bold tracking-[0.4em] text-amber-200">
            {code.code}
          </div>
          <div className="mt-1 text-xs text-muted">
            expires {new Date(code.expiresAt).toLocaleTimeString()}
          </div>
        </div>
      ) : (
        <button
          className="btn btn-secondary mt-4 w-full"
          onClick={regenerate}
          disabled={busy}
        >
          {busy
            ? 'Generating…'
            : display.status === 'PAIRING'
            ? 'Show pairing code'
            : 'Re-pair this display'}
        </button>
      )}

      {display.channelType === 'SCOREBOARD' && (
        <div className="mt-4 border-t border-border pt-4">
          <button
            onClick={() => setCustomizing((s) => !s)}
            className="flex w-full items-center justify-between text-sm text-muted hover:text-text"
          >
            <span>Scoreboard customization</span>
            <span className="font-mono text-xs">{customizing ? '▲' : '▼'}</span>
          </button>
          {customizing && (
            <CustomizePanel
              display={display}
              onSaved={async () => {
                await onChanged();
              }}
            />
          )}
        </div>
      )}
    </div>
  );
}

function CustomizePanel({
  display,
  onSaved,
}: {
  display: DisplaySummary;
  onSaved: () => void;
}) {
  const initial = display.displayConfig ?? {};
  const [theme, setTheme] = useState<ThemeKey>(
    (initial.theme as ThemeKey) ?? 'classic',
  );
  const [digitStyle, setDigitStyle] = useState<DigitStyle>(
    (initial.digitStyle as DigitStyle) ?? 'segment',
  );
  const [schoolName, setSchoolName] = useState(initial.schoolName ?? '');
  const [homeLogo, setHomeLogo] = useState(initial.homeLogo ?? '');
  const [awayLogo, setAwayLogo] = useState(initial.awayLogo ?? '');
  const [topName, setTopName] = useState(initial.sponsorTop?.name ?? '');
  const [topTag, setTopTag] = useState(initial.sponsorTop?.tagline ?? '');
  const [botName, setBotName] = useState(initial.sponsorBottom?.name ?? '');
  const [botTag, setBotTag] = useState(initial.sponsorBottom?.tagline ?? '');
  const [layoutTemplate, setLayoutTemplate] = useState(
    initial.layoutTemplate ?? '',
  );
  const [savedLayouts, setSavedLayouts] = useState<LayoutTemplate[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .listLayouts()
      .then(setSavedLayouts)
      .catch(() => setSavedLayouts([]));
  }, []);

  async function save() {
    setBusy(true);
    try {
      // Preserve existing top-level fields the card owns (assignedGameId)
      // so saving cosmetics doesn't accidentally unlink the game.
      const config: ScoreboardConfig = {
        ...(display.displayConfig ?? {}),
        theme,
        digitStyle,
        ...(schoolName ? { schoolName } : { schoolName: undefined }),
        ...(homeLogo ? { homeLogo } : { homeLogo: undefined }),
        ...(awayLogo ? { awayLogo } : { awayLogo: undefined }),
        ...(topName
          ? { sponsorTop: { name: topName, tagline: topTag || undefined } }
          : { sponsorTop: undefined }),
        ...(botName
          ? { sponsorBottom: { name: botName, tagline: botTag || undefined } }
          : { sponsorBottom: undefined }),
        layoutTemplate: layoutTemplate || undefined,
      };
      await api.updateDisplay(display.id, { displayConfig: config });
      await onSaved();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-4 space-y-4">
      <div>
        <label className="label">Scoreboard design</label>
        <select
          className="input"
          value={layoutTemplate}
          onChange={(e) => setLayoutTemplate(e.target.value)}
        >
          <option value="">Basic (default scoreboard)</option>
          <optgroup label="Template library">
            {TEMPLATE_OPTIONS.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </optgroup>
          {savedLayouts.length > 0 && (
            <optgroup label="My saved layouts">
              {savedLayouts.map((layout) => (
                <option key={layout.id} value={layout.id}>
                  {layout.name}
                </option>
              ))}
            </optgroup>
          )}
        </select>
      </div>

      <div>
        <label className="label">Theme</label>
        <div className="flex flex-wrap gap-2">
          {THEME_OPTIONS.map((t) => (
            <button
              key={t.value}
              type="button"
              onClick={() => setTheme(t.value)}
              className={`flex items-center gap-2 rounded-lg border px-3 py-1.5 text-xs font-semibold transition ${
                theme === t.value
                  ? 'border-brand bg-brand/10 text-text'
                  : 'border-border text-muted hover:text-text'
              }`}
            >
              <span
                className="h-3 w-3 rounded-full"
                style={{ background: t.swatch }}
              />
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="label">Digit style</label>
        <div className="flex gap-2">
          {(['segment', 'bulb', 'broadcast'] as DigitStyle[]).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setDigitStyle(s)}
              className={`rounded-lg border px-3 py-1.5 text-xs font-semibold transition ${
                digitStyle === s
                  ? 'border-brand bg-brand/10 text-text'
                  : 'border-border text-muted hover:text-text'
              }`}
            >
              {s === 'segment'
                ? '7-Segment'
                : s === 'bulb'
                  ? 'LED Bulbs'
                  : 'Broadcast'}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="label">School / event name (optional)</label>
        <input
          className="input"
          value={schoolName}
          onChange={(e) => setSchoolName(e.target.value)}
          placeholder="Springfield High"
          maxLength={100}
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label">Home logo</label>
          <input
            className="input text-2xl"
            value={homeLogo}
            onChange={(e) => setHomeLogo(e.target.value)}
            placeholder="🐾"
            maxLength={4}
          />
        </div>
        <div>
          <label className="label">Away logo</label>
          <input
            className="input text-2xl"
            value={awayLogo}
            onChange={(e) => setAwayLogo(e.target.value)}
            placeholder="🦅"
            maxLength={4}
          />
        </div>
      </div>

      <div className="space-y-2 rounded-lg border border-border bg-surface p-3">
        <div className="text-xs uppercase tracking-wider text-muted">
          Top sponsor strip
        </div>
        <input
          className="input"
          value={topName}
          onChange={(e) => setTopName(e.target.value)}
          placeholder="First National Bank"
          maxLength={60}
        />
        <input
          className="input"
          value={topTag}
          onChange={(e) => setTopTag(e.target.value)}
          placeholder="Proud Sponsor of Wildcat Athletics"
          maxLength={100}
        />
      </div>

      <div className="space-y-2 rounded-lg border border-border bg-surface p-3">
        <div className="text-xs uppercase tracking-wider text-muted">
          Bottom sponsor strip
        </div>
        <input
          className="input"
          value={botName}
          onChange={(e) => setBotName(e.target.value)}
          placeholder="Mike's Auto Body"
          maxLength={60}
        />
        <input
          className="input"
          value={botTag}
          onChange={(e) => setBotTag(e.target.value)}
          placeholder="Complete Collision Repair · 555-0199"
          maxLength={100}
        />
      </div>

      <button onClick={save} disabled={busy} className="btn btn-primary w-full">
        {busy ? 'Saving…' : 'Save customization'}
      </button>
      <p className="text-xs text-muted">
        Changes propagate to the display within ~5 seconds.
      </p>
    </div>
  );
}

function Row({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-muted">{label}</dt>
      <dd className="text-right text-text">{children}</dd>
    </div>
  );
}

function AddDisplayForm({
  venueId,
  onDone,
  onCancel,
}: {
  venueId: string;
  onDone: (created: CreateDisplayResponse) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState('');
  const [channelType, setChannelType] = useState<DisplayChannel>('SCOREBOARD');
  const [orientation, setOrientation] = useState<'landscape' | 'portrait'>(
    'landscape',
  );
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const created = await api.createDisplay(venueId, {
        name,
        channelType,
        orientation,
      });
      onDone(created);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'failed_to_create');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="card space-y-4">
      <h2 className="text-lg font-semibold">New display</h2>
      <div>
        <label className="label">Name</label>
        <input
          className="input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Main Gym Scoreboard, Lobby TV 1"
          required
          minLength={1}
        />
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <label className="label">Channel</label>
          <select
            className="input"
            value={channelType}
            onChange={(e) => setChannelType(e.target.value as DisplayChannel)}
          >
            {CHANNEL_OPTIONS.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="label">Orientation</label>
          <select
            className="input"
            value={orientation}
            onChange={(e) =>
              setOrientation(e.target.value as 'landscape' | 'portrait')
            }
          >
            <option value="landscape">Landscape</option>
            <option value="portrait">Portrait</option>
          </select>
        </div>
      </div>
      {err && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
          {err}
        </div>
      )}
      <div className="flex gap-2">
        <button className="btn btn-primary" disabled={busy}>
          {busy ? 'Creating…' : 'Create + show pairing code'}
        </button>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={onCancel}
          disabled={busy}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

function PairingCodeCard({
  data,
  onDismiss,
}: {
  data: CreateDisplayResponse;
  onDismiss: () => void;
}) {
  return (
    <div className="card border-brand/40 bg-brand/5">
      <div className="text-xs uppercase tracking-wider text-brand-light">
        Display created — pair it now
      </div>
      <h3 className="mt-1 text-lg font-semibold">{data.display.name}</h3>
      <p className="mt-2 text-sm text-muted">
        Open <span className="font-mono">display.scorelync.com</span> on the
        target screen and enter:
      </p>
      <div className="mt-4 rounded-xl border border-border bg-surface p-6 text-center">
        <div className="font-mono text-5xl font-bold tracking-[0.5em] text-brand-light">
          {data.pairingCode}
        </div>
        <div className="mt-2 text-xs text-muted">
          Expires at{' '}
          {new Date(data.pairingExpiresAt).toLocaleTimeString()} · code is
          single-use
        </div>
      </div>
      <button className="btn btn-secondary mt-4" onClick={onDismiss}>
        Done
      </button>
    </div>
  );
}
